/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {GatewayErrorCode} from '@fluxer/constants/src/GatewayConstants';
import {
	GatewayCloseCodeDescriptions,
	GatewayCloseCodes,
	GatewayErrorCodes,
} from '@fluxer/constants/src/GatewayConstants';

const RECONNECTABLE_GATEWAY_CLOSE_CODES = new Set<number>([
	GatewayCloseCodes.UNKNOWN_ERROR,
	GatewayCloseCodes.SESSION_TIMEOUT,
	GatewayCloseCodes.NOT_ELIGIBLE,
	GatewayCloseCodes.AT_CAPACITY,
	GatewayCloseCodes.ACK_BACKPRESSURE,
]);
const RESUMABLE_GATEWAY_CLOSE_CODES = new Set<number>([
	GatewayCloseCodes.UNKNOWN_ERROR,
	GatewayCloseCodes.SESSION_TIMEOUT,
]);
const GATEWAY_ERROR_CODE_SET = new Set<string>(Object.values(GatewayErrorCodes));

export interface ParsedGatewayErrorPayload {
	code: GatewayErrorCode;
	message: string;
}

export function isGatewayCloseCode(code: number): boolean {
	return code >= 4000 && code < 5000;
}

export function isReconnectableGatewayCloseCode(code: number): boolean {
	return RECONNECTABLE_GATEWAY_CLOSE_CODES.has(code);
}

export function isResumableGatewayCloseCode(code: number): boolean {
	return RESUMABLE_GATEWAY_CLOSE_CODES.has(code);
}

export function shouldClearGatewaySessionOnClose(code: number): boolean {
	return (
		code === GatewayCloseCodes.INVALID_SEQ ||
		(isGatewayCloseCode(code) && isReconnectableGatewayCloseCode(code) && !isResumableGatewayCloseCode(code))
	);
}

export function getGatewayCloseCodeDescription(code: number): string | null {
	return GatewayCloseCodeDescriptions[code] ?? null;
}

export function buildGatewayCloseLogSuffix(code: number, reason: string): string {
	const closeCodeDescription = getGatewayCloseCodeDescription(code);
	const closeDescriptionSuffix = closeCodeDescription == null ? '' : ` ${closeCodeDescription}`;
	const closeReasonSuffix = reason ? ` ${reason}` : '';
	return `${closeDescriptionSuffix}${closeReasonSuffix}`;
}

function isGatewayErrorCode(code: unknown): code is GatewayErrorCode {
	return typeof code === 'string' && GATEWAY_ERROR_CODE_SET.has(code);
}

export function parseGatewayErrorPayload(payload: unknown): ParsedGatewayErrorPayload | null {
	if (payload == null || typeof payload !== 'object') {
		return null;
	}

	const code = Reflect.get(payload, 'code');
	const message = Reflect.get(payload, 'message');
	if (!isGatewayErrorCode(code) || typeof message !== 'string') {
		return null;
	}

	return {
		code,
		message,
	};
}

export function parseAndEmitGatewayErrorPayload(
	payload: unknown,
	emit: (error: ParsedGatewayErrorPayload) => void,
): ParsedGatewayErrorPayload | null {
	const parsed = parseGatewayErrorPayload(payload);
	if (parsed == null) {
		return null;
	}
	emit(parsed);
	return parsed;
}
